<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="
                commentId
                    ? t('common.editGeneralComment')
                    : t('common.addGeneralComment')
            "
            click-shield
            class="component-comment-dialog"
        >
            <div class="component-comment-modal">
                <div class="component-comment-modal__textarea">
                    <VeeTextareaField
                        name="text"
                        :label="t('common.commentText')"
                    />
                </div>
                <div class="component-comment-modal__attachment">
                    <VeeFormFileInputField
                        name="file"
                        :max-file-size-in-bytes="maxFileSizeInBytes"
                        @delete-file="onDeleteFile"
                    />
                </div>
            </div>
            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('common.cancel') }}
            </pbl-button>
            <pbl-button
                slot="footer"
                :disabled="!meta.dirty || isMutating"
                :loading="isMutating"
                @click="onSubmit"
            >
                {{ t('common.save') }}
            </pbl-button>
        </pbl-dialog>
        <div id="component-comment-modal" />
    </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { computed, ref } from 'vue'
import { PebbleDialog } from '@pebble/core'
import { useField, useForm } from 'vee-validate'
import { AxiosError } from 'axios'
import VeeTextareaField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeTextareaField.vue'
import VeeFormFileInputField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormFileInputField.vue'
import { ComponentCommentOpenPayload, provideComponentCommentModal } from './useComponentCommentModal'
import { commentValidationSchema } from './ComponentCommentModal.form'
import { maxFileSizeInBytes } from '@/utils/file-utils'
import { useGlobalConfirmationModal } from '@/composables/useGlobalConfirmationModal'
import { useGeneralCommentsAddMutation } from '@/api/mutations/general-comments/useGeneralCommentsAddMutation'
import { useGeneralCommentsEditMutation } from '@/api/mutations/general-comments/useGeneralCommentsEditMutation'
import { ComponentType } from '@/configuration/component/component-types'
import { useGeneralCommentsDeleteFileMutation } from '@/api/mutations/general-comments/useGeneralCommentsDeleteFileMutation'
import { AttachmentDto } from '@/api/generated/HCA'
import { useGeneralCommentsUploadFileMutation } from '@/api/mutations/general-comments/useGeneralCommentsUploadFileMutation'

const { t } = useI18n()
const toasts = usePebbleToasts()

const triggerGlobalConfirmationModal = useGlobalConfirmationModal()
const dialogRef = ref<PebbleDialog | null>(null)

const props = defineProps<{
    componentId: string
    componentType: ComponentType
}>()

const { handleSubmit, meta, resetForm, setFieldValue, resetField } = useForm({
    validationSchema: commentValidationSchema,
    initialValues: {
        commentId: undefined,
        file: undefined,
        text: '',
    },
})

const { value: commentId } = useField<number | undefined>(() => 'commentId')
const { value: file } = useField<AttachmentDto | undefined>(() => 'file')

const { mutateAsync: mutateAddAsync, isPending: isAddPending } = useGeneralCommentsAddMutation(props.componentType)
const { mutateAsync: mutateEditAsync, isPending: isEditPending } = useGeneralCommentsEditMutation(props.componentType)
const { mutateAsync: mutateDeleteAttachmentAsync, isPending: isDeleteAttachmentPending } = useGeneralCommentsDeleteFileMutation(props.componentType)
const { mutateAsync: mutateUploadAttachmentAsync, isPending: isUploadAttachmentPending } = useGeneralCommentsUploadFileMutation(props.componentType)

const isMutating = computed(() => isAddPending.value || isEditPending.value || isDeleteAttachmentPending.value || isUploadAttachmentPending.value)

const openComment = (payload?: ComponentCommentOpenPayload) => {
    resetForm({
        values: {
            commentId: payload?.id,
            text: payload?.text ?? '',
            file: payload?.attachment ?? undefined,
        },
    })
    dialogRef.value?.show()
}

const closeModal = () => {
    dialogRef.value?.hide()
}

const onSubmit = handleSubmit(async (values) => {
    try {
        if (values.commentId) {
            const promises = [mutateEditAsync({
                generalCommentId: values.commentId,
                text: values.text,
                generalCommentComponentSid: props.componentId,
            })]
            if (values.file && values.file instanceof File) {
                promises.push(mutateUploadAttachmentAsync({
                    file: values.file as File,
                    commentId: values.commentId,
                    generalCommentComponentSid: props.componentId,
                }))
            }
            await Promise.all(promises)
        } else {
            await mutateAddAsync({
                file: values.file as File,
                generalCommentText: values.text,
                generalCommentComponentSid: props.componentId,
            })
        }
        closeModal()
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
})

const onDeleteFile = () => {
    triggerGlobalConfirmationModal({
        headline: t('common.deleteAttachment.headline'),
        content: t('common.deleteAttachment.content'),
        confirmButtonText: t('common.continue'),
        target: '#component-comment-modal',
        onConfirm: async () => {
            if (!file.value?.id) {
                resetField('file', {value: undefined})
            } else {
                await mutateDeleteAttachmentAsync({
                    generalCommentComponentSid: props.componentId,
                    fileId: file.value?.id as number,
                }).then(() => {
                    setFieldValue('file', undefined)
                })
            }
        },
    })
}

provideComponentCommentModal({
    openComment,
})
</script>

<style scoped lang="scss">
.component-comment-dialog {
    --width: 70vw;
}

.component-comment-modal {
    display: flex;
    flex-direction: column;

    gap: $spacing-xs;
}
</style>
